<template>
  <router-link :to="cmsPageDetailsLink(blogPost)" class="post-card">
    <div class="post-card-media">
      <strapi-components-media-image
        v-if="postImage"
        :image="postImage"
        :alt="blogPost.title"
        class="post-card-image"
        sizes="xs:100vw sm:100vw md:360px lg:360px"
        :preload="mediaPreload"
      />
      <media-image-placeholder
        v-else
        class="post-card-image__placeholder"
        sizes="xs:100vw sm:100vw md:360px lg:360px"
        :preload="mediaPreload"
      />
      <general-badge
        v-if="blogPost?.cms_category?.data"
        class="post-card-badge"
      >
        {{ blogPost.cms_category.data.attributes.name }}
      </general-badge>
    </div>
    <div class="post-card-content">
      <h2 v-if="!term" class="post-card-title" v-html="blogPost.title" />
      <h2
        v-else
        class="post-card-title"
        v-html="highlightSearchedValue(blogPost.title, term)"
      />
      <span class="post-card-date">{{ formattedDate }}</span>
      <span class="post-card-description">
        {{ blogPost.subtitle }}
      </span>
      <span class="post-card-action">
        {{ $t("Read more") }}
      </span>
    </div>
  </router-link>
</template>

<script lang="ts" setup>
import type { ComputedRef } from "vue";
import type { CmsPage } from "~/types/strapi/collection-types/CmsPage";
import { POST_TERM_INJECT_TOKEN } from "~/modules/blog/variables";
import type { StrapiImage } from "~/types/strapi";
import type { MediaFetchPriority } from "~/modules/media/types/MediaFetchPriority";

const { cmsPageDetailsLink } = useLinker();

const blog = defineProps<{
  blogPost: CmsPage;
  mediaPreload?: MediaFetchPriority;
}>();

const formattedDate = computed<string>(() =>
  new Date(blog.blogPost.publishedAt as string).toLocaleDateString(),
);

const postImage = computed<StrapiImage | undefined>(() => {
  return blog.blogPost.image?.data?.attributes ?? undefined;
});

const term: ComputedRef<string> | undefined = inject(
  POST_TERM_INJECT_TOKEN,
  undefined,
);

useCacheTagger((addCacheTags) => {
  addCacheTags(cmsTag(blog.blogPost));
});
</script>

<style lang="scss">
.post-card {
  $post-card-base: &;

  @apply flex h-full w-full max-w-full flex-col justify-start lg:p-3;
  @apply bg-transparent hover:cursor-pointer hover:bg-white hover:shadow-lg;
  transition: 0.3s ease box-shadow;

  &:hover {
    #{$post-card-base}-action {
      @apply text-secondary-700 underline;
    }
  }

  &-media {
    @apply relative;
  }

  &-image {
    @apply aspect-[2.4/1] h-auto w-full object-cover;

    &__placeholder {
      @apply aspect-[2.4/1] h-auto w-full object-cover;
    }
  }

  &-badge {
    @apply absolute left-1 top-1 bg-primary-100 px-2 py-1 text-xs font-normal normal-case text-primary-700;
  }

  &-content {
    @apply mt-3 flex flex-col justify-start gap-2;
  }

  &-title {
    @apply break-words font-semibold leading-5 text-neutral-900;
  }

  &-date {
    @apply text-xs font-semibold text-neutral-500;
  }

  &-description {
    @apply line-clamp-3 max-h-[60px] text-ellipsis text-sm text-neutral-500;
  }

  &-action {
    @apply text-sm text-link;
  }
}
</style>
